/**
 * SchemaValidator.js
 * Lightweight data integrity guard for all JSONL clinical nodes.
 *
 * Supports two schema styles:
 *   1) Primary: option.resultado === 'correcto' (most JSONL files)
 *   2) Alternative: top-level `respuesta_correcta` field
 */
export class SchemaValidator {
    static validateNode(node) {
        if (!node.id) {
            console.error(`[SchemaValidator] Node missing 'id':`, node);
            return false;
        }
        if (!node.enunciado && !node.pregunta && !node.prompt) {
            console.error(`[SchemaValidator] Node ${node.id} missing text field ('enunciado', 'pregunta' or 'prompt').`);
            return false;
        }
        if (!Array.isArray(node.opciones) || node.opciones.length === 0) {
            console.error(`[SchemaValidator] Node ${node.id} has no options.`);
            return false;
        }

        // Accept either schema style for identifying the correct answer
        const hasCorrect = node.respuesta_correcta
            ? node.opciones.some(o => o.id === node.respuesta_correcta)
            : node.opciones.some(o => o.resultado === 'correcto');

        if (!hasCorrect) {
            console.error(`[SchemaValidator] Node ${node.id}: no correct answer found.`);
            return false;
        }

        return true;
    }

    static sanitize(node) {
        const orig = node.original || {};

        // Bridge legacy/alternative field names to canonical schema
        // Fallback to 'original' if top-level is missing/null
        if (!node.justificacion) node.justificacion = node.explanation || orig.justificacion || orig.explanation;
        if (!node.topic) node.topic = node.tema || orig.topic || orig.tema;
        if (!node.subtopic) node.subtopic = node.subtema || orig.subtopic || orig.subtema;
        if (!node.enunciado) {
            node.enunciado = node.prompt || node.pregunta || orig.enunciado || orig.prompt || orig.pregunta;
        }
        
        if (!node.tags || node.tags.length === 0) node.tags = orig.tags || [];
        if (!node.difficulty) node.difficulty = node.dificultad || orig.difficulty || orig.dificultad || 1;

        // Ensure difficulty is numeric
        if (typeof node.difficulty === 'string') {
            const val = node.difficulty.toLowerCase();
            const diffMap = { 'baja': 1, 'media': 2, 'alta': 3, 'easy': 1, 'standard': 2, 'hard': 3 };
            node.difficulty = diffMap[val] || parseInt(val) || 1;
        }
        node.difficulty = Number(node.difficulty) || 1;

        // Derive respuesta_correcta if missing
        if (!node.respuesta_correcta) {
            node.respuesta_correcta = orig.respuesta_correcta;
        }
        
        if (!node.respuesta_correcta) {
            const correct = node.opciones.find(o => o.resultado === 'correcto' || o.outcome === 'correct');
            if (correct) node.respuesta_correcta = correct.id;
        }

        // Mark as lethal if either node or original says so
        if (node.letal_si_falla === null || node.letal_si_falla === undefined) {
            node.letal_si_falla = orig.letal_si_falla || false;
        }

        return node;
    }
}
